import React, { useState } from "react";
import { Sparkles, Loader2, Save, X, Wand2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { industryCurate, industryCurateJobStatus, industryBulkSave } from "@/lib/api";

/**
 * Assistant Curator Mode
 *
 * Modal that lets the admin describe an industry once. The LLM then proposes
 * an expert hint per department. Admin can edit each block, prune the list,
 * then save them all at once into the Industry Library.
 */
export default function IndustryCuratorModal({ open, onClose, onSaved, defaultIndustries }) {
  const [stage, setStage] = useState("input"); // input | loading | review | error
  const [slug, setSlug] = useState("");
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [proposals, setProposals] = useState([]); // [{department, content, included}]
  const [llmMeta, setLlmMeta] = useState(null);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const pollRef = React.useRef(null);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const reset = () => {
    stopPolling();
    setStage("input");
    setSlug("");
    setLabel("");
    setDescription("");
    setProposals([]);
    setLlmMeta(null);
    setErrorMsg("");
  };

  React.useEffect(() => () => stopPolling(), []);

  if (!open) return null;

  const generate = async () => {
    if (!slug.trim() || !label.trim() || description.trim().length < 30) {
      toast.error("Slug, etiqueta y descripción (>30 caracteres) son obligatorios.");
      return;
    }
    setStage("loading");
    setErrorMsg("");
    try {
      const queued = await industryCurate({
        industry_slug: slug.trim().toLowerCase(),
        industry_label: label.trim(),
        description: description.trim(),
      });
      const jobId = queued?.job?.id;
      if (!jobId) {
        throw new Error("No se recibió job_id del servidor.");
      }
      // Poll every 3s; stop when status is done/error
      pollRef.current = setInterval(async () => {
        try {
          const { job } = await industryCurateJobStatus(jobId);
          if (!job) return;
          if (job.status === "done") {
            stopPolling();
            const r = job.result || {};
            setLlmMeta(r.llm || null);
            setProposals((r.departments || []).map((x) => ({ ...x, included: true })));
            setStage("review");
          } else if (job.status === "error") {
            stopPolling();
            setErrorMsg(job.error || "El LLM falló sin detalle.");
            setStage("error");
          }
        } catch (e) {
          stopPolling();
          setErrorMsg(e?.response?.data?.detail || e.message || "Error consultando el job.");
          setStage("error");
        }
      }, 3000);
    } catch (e) {
      setErrorMsg(e?.response?.data?.detail || e.message || "No se pudo encolar el job.");
      setStage("error");
    }
  };

  const save = async () => {
    const items = proposals.filter((p) => p.included && p.content.trim());
    if (!items.length) {
      toast.error("Seleccione al menos un departamento con contenido.");
      return;
    }
    setSaving(true);
    try {
      const d = await industryBulkSave({
        industry_slug: slug.trim().toLowerCase(),
        industry_label: label.trim(),
        items: items.map(({ department, content }) => ({ department, content })),
      });
      toast.success(`${d.count} hints guardados para ${label}.`);
      reset();
      onClose();
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error guardando.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/70 p-4"
         data-testid="curator-modal">
      <div className="w-full max-w-4xl rounded-sm bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-slate-200 p-6">
          <div>
            <p className="eyebrow flex items-center gap-1.5 text-accent-purple">
              <Sparkles className="h-3 w-3" /> Assistant Curator Mode
            </p>
            <h2 className="mt-1 font-display text-2xl text-ink">
              Auto-generar conocimiento por industria
            </h2>
            <p className="mt-1 max-w-xl text-[12px] text-slate-500">
              Describa la industria una sola vez. El LLM redactará un bloque
              experto por departamento con vocabulario nativo (reguladores,
              sistemas, KPIs, integraciones).
            </p>
          </div>
          <button onClick={() => { reset(); onClose(); }} className="text-slate-400 hover:text-ink"
                  data-testid="curator-close-btn">
            <X className="h-5 w-5" />
          </button>
        </div>

        {stage === "input" && (
          <div className="space-y-4 p-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Slug del sector</label>
                <Input value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
                       placeholder="agritech, fintech, seguros…"
                       className="mt-1 rounded-sm border-slate-300 font-mono"
                       data-testid="curator-slug-input" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Etiqueta visible</label>
                <Input value={label} onChange={(e) => setLabel(e.target.value)}
                       placeholder="AgriTech / Agricultura digital"
                       className="mt-1 rounded-sm border-slate-300"
                       data-testid="curator-label-input" />
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Descripción del sector</label>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)}
                        rows={8}
                        placeholder="Empresa de tecnología agrícola en LATAM. Vendemos software de gestión a productores cafeteros, plataformas IoT para invernaderos, drones de fumigación, trazabilidad blockchain de cacao. Atendemos 800 fincas en Colombia, Perú y Ecuador. Equipo de 120 personas. Retos: regulación fitosanitaria, conectividad rural, financiamiento a productores…"
                        className="mt-1 rounded-sm border-slate-300 text-[12px] leading-relaxed"
                        data-testid="curator-description-textarea" />
              <p className="mt-1 text-[10px] text-slate-500">
                {description.length} caracteres · mínimo 30
              </p>
            </div>

            {defaultIndustries && defaultIndustries.length > 0 && (
              <div className="rounded-sm border border-slate-200 bg-slate-50 p-3 text-[11px] text-slate-600">
                <strong>Sectores ya en biblioteca:</strong>{" "}
                {defaultIndustries.map((i) => i.label).join(" · ")}
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => { reset(); onClose(); }}
                      className="rounded-sm border-slate-300 text-[11px] uppercase tracking-[0.18em]">
                Cancelar
              </Button>
              <Button onClick={generate}
                      className="rounded-sm bg-gradient-to-r from-accent-blue to-accent-purple text-[11px] uppercase tracking-[0.18em] text-white hover:opacity-95"
                      data-testid="curator-generate-btn">
                <Wand2 className="mr-2 h-3.5 w-3.5" /> Generar conocimiento
              </Button>
            </div>
          </div>
        )}

        {stage === "loading" && (
          <div className="flex flex-col items-center justify-center gap-3 px-6 py-16">
            <Loader2 className="h-8 w-8 animate-spin text-accent-blue" />
            <p className="font-display text-lg text-ink">Curando conocimiento experto…</p>
            <p className="max-w-md text-center text-[12px] text-slate-500">
              El LLM está redactando bloques densos para cada departamento.
              Esto puede tomar 1-3 minutos. Esta ventana se actualizará sola
              cuando termine.
            </p>
          </div>
        )}

        {stage === "error" && (
          <div className="flex flex-col items-center justify-center gap-3 px-6 py-16">
            <X className="h-8 w-8 text-red-500" />
            <p className="font-display text-lg text-ink">No se pudo generar</p>
            <p className="max-w-xl text-center text-[12px] text-slate-600"
               data-testid="curator-error">
              {errorMsg}
            </p>
            <Button onClick={() => setStage("input")}
                    className="mt-3 rounded-sm border border-slate-300 bg-white text-[11px] uppercase tracking-[0.18em] text-ink hover:bg-slate-50">
              ← Reintentar
            </Button>
          </div>
        )}

        {stage === "review" && (
          <div className="flex max-h-[70vh] flex-col">
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-3">
              <p className="text-[11px] text-slate-500">
                <strong className="text-ink">{proposals.length}</strong> bloques
                generados {llmMeta && <>· <span className="font-mono text-accent-blue">{llmMeta.provider}/{llmMeta.model}</span></>}
              </p>
              <div className="flex items-center gap-2 text-[11px]">
                <button onClick={() => setProposals((ps) => ps.map((p) => ({ ...p, included: true })))}
                        className="text-accent-blue hover:underline">marcar todos</button>
                <span className="text-slate-300">|</span>
                <button onClick={() => setProposals((ps) => ps.map((p) => ({ ...p, included: false })))}
                        className="text-slate-500 hover:text-ink">desmarcar todos</button>
              </div>
            </div>
            <div className="flex-1 space-y-3 overflow-y-auto p-6">
              {proposals.map((p, idx) => (
                <article key={idx}
                         className={`border ${p.included ? "border-accent-blue/40 bg-accent-blue/5" : "border-slate-200 bg-slate-50/50"} p-4 transition`}
                         data-testid={`curator-proposal-${idx}`}>
                  <div className="flex items-start justify-between gap-3">
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={p.included}
                             onChange={(e) => setProposals((ps) => ps.map((x, i) => i === idx ? { ...x, included: e.target.checked } : x))}
                             className="h-4 w-4 accent-accent-blue"
                             data-testid={`curator-include-${idx}`} />
                      <span className="font-display text-lg text-ink">{p.department}</span>
                    </label>
                    <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-slate-400">
                      {p.content.length} chars
                    </span>
                  </div>
                  <Textarea
                    value={p.content}
                    onChange={(e) => setProposals((ps) => ps.map((x, i) => i === idx ? { ...x, content: e.target.value } : x))}
                    rows={5}
                    disabled={!p.included}
                    className="mt-2 rounded-sm border-slate-300 font-mono text-[12px] leading-relaxed disabled:bg-slate-100"
                    data-testid={`curator-content-${idx}`}
                  />
                </article>
              ))}
            </div>
            <div className="flex items-center justify-between border-t border-slate-200 px-6 py-4">
              <Button variant="outline" onClick={() => setStage("input")}
                      className="rounded-sm border-slate-300 text-[11px] uppercase tracking-[0.18em]">
                ← Volver y reformular
              </Button>
              <Button onClick={save} disabled={saving}
                      className="rounded-sm bg-emerald-600 text-[11px] uppercase tracking-[0.18em] text-white hover:bg-emerald-700"
                      data-testid="curator-save-btn">
                {saving ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-2 h-3.5 w-3.5" />}
                Guardar {proposals.filter((p) => p.included).length} bloques
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
